import { fg } from '@atlaskit/platform-feature-flags/fg';
import { navigateToTeamsApp } from '@atlaskit/teams-app-config/utils/teams-app-navigation/navigate-to-teams-app';
import type { ActivityClient } from '@atlassian/recent-work-client/base';

import { NLPSearchErrorState } from '../../../ai-answer-dialog/nlp-search-error-state';
import { NLPSearchResultFormat } from '../../../ai-answer-dialog/nlp-search-result-format';
import type {
	NLPSearchPersonResultType,
	NLPSearchWhoResultCollaboratorDataType,
	NLPSearchWhoResultDataType,
	NLPSearchWorkResultRecentActivityDataType,
	SearchAIAnswerQueryType,
	TraceHeaders,
} from '../../../ai-answer-dialog/types';
import { createAtlAttributionHeader } from '../../../common/fetch/atl-attribution-header';
import { fetchAggResponse } from '../../../common/fetch/fetch-agg-response';
import { logger } from '../../../common/utils/logger';
import { SAINFeatureControl } from '../../../common/utils/sain-feature-control';
import { trimARI } from '../../trim-ari';
import { formatTimeKnowledgeCard, sanitizeAndMapRecentWorkActivity } from '../../util';
import {
	KNOWLEDGE_CARD_PRIMARY_USER_OPERATION_NAME,
	KNOWLEDGE_CARD_USER_SEARCH_OPERATION_NAME,
	KnowledgeCardPrimaryUserDataQuery,
	type KnowledgeCardPrimaryUserDataQueryType,
	type KnowledgeCardPrimaryUserDataVariables,
	KnowledgeCardUserSearchQuery,
	type KnowledgeCardUserSearchQueryType,
	type KnowledgeCardUserSearchVariables,
} from './gql';

type fetchPersonKnowledgeCardWithPostFilteringVariables = {
	personQuery: string;
	tenantId: string;
	principalUserId?: string;
	sourceProduct?: string;
	workspaceId: string;
	traceHeaders?: TraceHeaders;
};

export const fetchPersonKnowledgeCardDataWithPostFiltering = ({
	personQuery,
	tenantId,
	sourceProduct,
	workspaceId,
	traceHeaders,
}: fetchPersonKnowledgeCardWithPostFilteringVariables): Promise<SearchAIAnswerQueryType> => {
	let responseTraceId: string | undefined;
	let responseRequestId: string | undefined;
	let responseError: Error | undefined;
	const siteARI = sourceProduct ? `ari:cloud:${sourceProduct}::site/${tenantId}` : '';
	return fetchAggResponse<KnowledgeCardUserSearchVariables, KnowledgeCardUserSearchQueryType>({
		graphQuery: KnowledgeCardUserSearchQuery,
		variables: {
			userQuery: personQuery,
			siteId: siteARI,
		},
		operationName: KNOWLEDGE_CARD_USER_SEARCH_OPERATION_NAME,
		headers: {
			...createAtlAttributionHeader({
				atlWorkspaceId: workspaceId,
				tenantId,
				product: sourceProduct ?? '',
			}),
			...(traceHeaders ?? undefined),
		},
	})
		.then(({ data, errors, gatewayTracingHeaders }) => {
			responseTraceId = gatewayTracingHeaders?.['x-trace-id'];
			responseRequestId = gatewayTracingHeaders?.['atl-request-id'];
			const users = data?.knowledgeDiscovery?.searchUser?.users || [];
			const message = data?.knowledgeDiscovery?.searchUser?.message;
			if (errors || message) {
				responseError = new Error(JSON.stringify({ errors, message }));
				throw responseError;
			}
			const mappedResults = users.map((user) => ({
				id: user.id,
				name: user.name,
				avatarUrl: user.avatarUrl || '',
				title: user.title || '',
				zoneInfo: user.zoneInfo || '',
				location: user.location || '',
			}));
			const finalResult: NLPSearchPersonResultType = {
				user_metadata_only_cards: mappedResults,
			};
			return {
				nlpSearch: {
					nlpResult: JSON.stringify(finalResult),
					uniqueSources: [],
					disclaimer: null,
					errorState: null,
					format: NLPSearchResultFormat.JSON,
				},
			};
		})
		.catch((error) => {
			if (fg('kd_search_ai_robust_sentry_logs') || fg('kd_enable_networkerror_logging')) {
				logger.error({
					feature: 'person-knowledge-card',
					element: 'container',
					operation: 'fetch',
					category: responseError && error === responseError ? 'graphql' : 'unknown',
					message: 'Knowledge card request failed',
					traceId: responseTraceId ?? traceHeaders?.['X-B3-TraceId'],
					requestId: responseRequestId,
					error: responseError && error === responseError ? undefined : error,
					legacyError: error,
					functionName: 'fetchPersonKnowledgeCardDataWithPostFiltering',
				});
			}
			return Promise.resolve({
				nlpSearch: {
					errorState: NLPSearchErrorState.NetworkError,
					disclaimer: null,
					format: NLPSearchResultFormat.JSON,
					uniqueSources: [],
				},
			});
		});
};

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const fetchPersonKnowledgeCardDataWithPostFilteringAndRequestId = async ({
	personQuery,
	tenantId,
	sourceProduct,
	workspaceId,
	traceHeaders,
}: fetchPersonKnowledgeCardWithPostFilteringVariables): Promise<SearchAIAnswerQueryType> => {
	let responseTraceId: string | undefined;
	let responseRequestId: string | undefined;
	let responseError: Error | undefined;
	const siteARI = sourceProduct ? `ari:cloud:${sourceProduct}::site/${tenantId}` : '';
	return fetchAggResponse<KnowledgeCardUserSearchVariables, KnowledgeCardUserSearchQueryType>({
		graphQuery: KnowledgeCardUserSearchQuery,
		variables: {
			userQuery: personQuery,
			siteId: siteARI,
		},
		operationName: KNOWLEDGE_CARD_USER_SEARCH_OPERATION_NAME,
		headers: {
			...createAtlAttributionHeader({
				atlWorkspaceId: workspaceId,
				tenantId,
				product: sourceProduct ?? '',
			}),
			...(traceHeaders ?? undefined),
		},
	})
		.then(({ data, errors, gatewayTracingHeaders }) => {
			responseTraceId = gatewayTracingHeaders?.['x-trace-id'];
			responseRequestId = gatewayTracingHeaders?.['atl-request-id'];
			const users = data?.knowledgeDiscovery?.searchUser?.users || [];
			const message = data?.knowledgeDiscovery?.searchUser?.message;
			if (errors || message) {
				responseError = new Error(JSON.stringify({ errors, message }));
				throw responseError;
			}
			const mappedResults = users.map((user) => ({
				id: user.id,
				name: user.name,
				avatarUrl: user.avatarUrl || '',
				title: user.title || '',
				zoneInfo: user.zoneInfo || '',
				location: user.location || '',
			}));
			const finalResult: NLPSearchPersonResultType = {
				user_metadata_only_cards: mappedResults,
			};
			return {
				nlpSearch: {
					nlpResult: JSON.stringify(finalResult),
					uniqueSources: [],
					disclaimer: null,
					errorState: null,
					format: NLPSearchResultFormat.JSON,
					extraAPIAnalyticsAttributes: {
						requestId: gatewayTracingHeaders?.['atl-request-id'] ?? null,
						traceId: gatewayTracingHeaders?.['x-trace-id'] ?? null,
					},
				},
			};
		})
		.catch((error) => {
			if (fg('kd_search_ai_robust_sentry_logs') || fg('kd_enable_networkerror_logging')) {
				logger.error({
					feature: 'person-knowledge-card',
					element: 'container',
					operation: 'fetch',
					category: responseError && error === responseError ? 'graphql' : 'unknown',
					message: 'Knowledge card request failed',
					traceId: responseTraceId ?? traceHeaders?.['X-B3-TraceId'],
					requestId: responseRequestId,
					error: responseError && error === responseError ? undefined : error,
					legacyError: error,
					functionName: 'fetchPersonKnowledgeCardDataWithPostFiltering',
				});
			}
			return Promise.resolve({
				nlpSearch: {
					errorState: NLPSearchErrorState.NetworkError,
					disclaimer: null,
					format: NLPSearchResultFormat.JSON,
					uniqueSources: [],
				},
			});
		});
};

type fetchPrimaryUserDataVariables = {
	accountId: string;
	tenantId: string;
	requestingUserId: string;
	activityClient: ActivityClient;
	locale: string;
};

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const fetchPrimaryUserData = ({
	accountId,
	tenantId,
	requestingUserId,
	activityClient,
	locale,
	workspaceId,
	product,
	traceHeaders,
}: fetchPrimaryUserDataVariables & {
	workspaceId: string;
	product: string;
	traceHeaders?: TraceHeaders;
}): Promise<NLPSearchWhoResultDataType> => {
	const featureServiceModel = SAINFeatureControl.getCollabGraphExperimentModel();

	const aggPromise = fetchAggResponse<
		KnowledgeCardPrimaryUserDataVariables,
		KnowledgeCardPrimaryUserDataQueryType
	>({
		graphQuery: KnowledgeCardPrimaryUserDataQuery,
		variables: {
			accountId,
			accountIdARI: `ari:cloud:identity::user/${accountId}`,
			tenantId,
			requestingUserId,
			featureServiceModel,
		},
		operationName: KNOWLEDGE_CARD_PRIMARY_USER_OPERATION_NAME,
		headers: {
			...createAtlAttributionHeader({
				atlWorkspaceId: workspaceId,
				tenantId,
				product: product ?? '',
			}),
			...(traceHeaders ?? undefined),
		},
	});
	const activityPromise = activityClient.fetchActivities(
		[100],
		{
			limit: 50,
			actors: [`ari:cloud:identity::user/${accountId}`],
		},
		['workedOn'],
		false,
	);

	const toUserProfile = (toAccountId: string) =>
		navigateToTeamsApp({
			type: 'USER',
			payload: {
				userId: toAccountId,
			},
			cloudId: tenantId,
		}).href;

	// Handle the primary user data first, then attempt to fetch activity data
	return Promise.allSettled([aggPromise, activityPromise]).then((results) => {
		const [aggResult, activityResult] = results;

		// Handle GraphQL call failure
		if (aggResult.status === 'rejected') {
			throw new Error('Failed to fetch user data');
		}

		const aggResponse = aggResult.value;

		// Create the base user data structure
		const primaryUserData = {
			account_id: accountId,
			name: aggResponse.data?.user.name || '',
			job_title: aggResponse.data?.user.extendedProfile?.jobTitle,
			profile_url: fg('platform-adopt-teams-nav-config')
				? toUserProfile(accountId)
				: `/people/${accountId}`,
			avatar_url: aggResponse.data?.user.picture,
			email: aggResponse.data?.user.email,
			location: aggResponse.data?.user.extendedProfile?.location,
			department: aggResponse.data?.user.extendedProfile?.department,
			organization: aggResponse.data?.user.extendedProfile?.organization,
			time: aggResponse.data?.user.zoneinfo
				? formatTimeKnowledgeCard({ locale, zoneInfo: aggResponse.data.user.zoneinfo })
				: undefined,
			collaborators: aggResponse.data?.smarts.recommendedUser.reduce<
				NLPSearchWhoResultCollaboratorDataType[]
			>((acc, node) => {
				if (trimARI(node.user.id) !== accountId) {
					acc.push({
						name: node.user.name,
						picture: node.user.picture,
						title: !!node.user.extendedProfile?.jobTitle
							? node.user.extendedProfile.jobTitle
							: null,
						url: !!node.user.id
							? fg('platform-adopt-teams-nav-config')
								? toUserProfile(trimARI(node.user.id))
								: `/people/${trimARI(node.user.id)}`
							: null,
						accountId: node.user.id,
						department: !!node.user.extendedProfile?.department
							? node.user.extendedProfile.department
							: null,
						email: !!node.user.email ? node.user.email : null,
					});
				}
				return acc;
			}, []),
			viewAllLink: fg('platform-adopt-teams-nav-config')
				? toUserProfile(accountId)
				: `/people/${accountId}`,
			recentActivity: [] as NLPSearchWorkResultRecentActivityDataType[],
		};

		// Add activity data if the call succeeded
		if (
			activityResult.status === 'fulfilled' &&
			activityResult.value.workedOn &&
			activityResult.value.workedOn.length > 0
		) {
			primaryUserData.recentActivity = sanitizeAndMapRecentWorkActivity(
				activityResult.value.workedOn,
			);
		}

		return primaryUserData;
	});
};
